import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import agent from '../api/axios';
import type { InviteDetails } from '../types/api';
import { useSnackbar } from 'notistack';
import { useAuth } from '../auth/AuthContext';

const schema = yup.object().shape({
  password: yup
    .string()
    .min(8, 'Password must be at least 8 characters')
    .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
    .matches(/[0-9]/, 'Password must contain at least one number')
    .required('Password is required'),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref('password')], 'Passwords must match')
    .required('Please confirm your password'),
});

interface FormData {
  password: string;
  confirmPassword: string;
}

export default function AcceptInvite() {
  const { enqueueSnackbar } = useSnackbar();
  const { login } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [inviteDetails, setInviteDetails] = useState<InviteDetails | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    const fetchInviteDetails = async () => {
      if (!token) {
        setError('Invalid invitation link. No token provided.');
        setLoading(false);
        return;
      }

      try {
        const details = await agent.Team.getInviteDetails(token);
        if (details.isExpired) {
          setError(
            'This invitation has expired. Please contact your administrator for a new invitation.'
          );
        } else {
          setInviteDetails(details);
        }
      } catch (err: any) {
        const message =
          err.response?.data?.message || 'Invalid or expired invitation';
        setError(message);
      } finally {
        setLoading(false);
      }
    };

    fetchInviteDetails();
  }, [token]);

  const onSubmit = async (data: FormData) => {
    if (!token) return;

    setSubmitting(true);
    try {
      const response = await agent.Team.acceptInvite({
        token,
        password: data.password,
      });

      // Update Redux auth state and redirect to dashboard
      login(response.token);
      enqueueSnackbar('Welcome! Your account has been created successfully.', {
        variant: 'success',
      });
      navigate('/dashboard', { replace: true });
    } catch (err: any) {
      const message =
        err.response?.data?.message || 'Failed to accept invitation';
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        bgcolor="#f5f5f5"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        bgcolor="#f5f5f5"
        p={3}
      >
        <Paper sx={{ p: 4, maxWidth: 400, textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom color="error">
            Invitation Error
          </Typography>
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
          <Button variant="contained" onClick={() => navigate('/login')}>
            Go to Login
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      bgcolor="#f5f5f5"
      p={3}
    >
      <Paper sx={{ p: 4, maxWidth: 450, width: '100%' }}>
        <Typography variant="h4" gutterBottom textAlign="center">
          Welcome to SimplyPayroll
        </Typography>

        {inviteDetails && (
          <Box mb={3}>
            <Alert severity="info">
              <Typography variant="body2">
                <strong>{inviteDetails.businessName}</strong> has invited you to
                join their team as a <strong>{inviteDetails.role}</strong>.
              </Typography>
            </Alert>

            <Box mt={2} p={2} bgcolor="#f9f9f9" borderRadius={1}>
              <Typography variant="body2" color="textSecondary">
                Setting up account for:
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {inviteDetails.firstName}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {inviteDetails.email}
              </Typography>
            </Box>
          </Box>
        )}

        <Typography variant="body2" color="textSecondary" mb={3}>
          Create a password to complete your account setup.
        </Typography>

        <form onSubmit={handleSubmit(onSubmit)}>
          <TextField
            {...register('password')}
            type="password"
            label="Password"
            fullWidth
            margin="normal"
            error={!!errors.password}
            helperText={errors.password?.message}
            autoFocus
          />

          <TextField
            {...register('confirmPassword')}
            type="password"
            label="Confirm Password"
            fullWidth
            margin="normal"
            error={!!errors.confirmPassword}
            helperText={errors.confirmPassword?.message}
          />

          <Typography
            variant="caption"
            color="textSecondary"
            display="block"
            mt={1}
            mb={2}
          >
            Password must be at least 8 characters with uppercase, lowercase,
            and a number.
          </Typography>

          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            size="large"
            disabled={submitting}
            sx={{ mt: 2 }}
          >
            {submitting ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Create Account'
            )}
          </Button>
        </form>

        <Box mt={3} textAlign="center">
          <Typography variant="body2" color="textSecondary">
            Already have an account?{' '}
            <Button
              variant="text"
              size="small"
              onClick={() => navigate('/login')}
            >
              Sign In
            </Button>
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
}
